/** @format */

import {
	Box,
	Button,
	Container,
	Flex,
	Grid,
	GridItem,
	Link,
	Text,
} from '@chakra-ui/react';
import {decode} from 'html-entities';
import {colors} from '../../styles/js/colors';
import {textStyles} from '../../styles/js/textStyles';
import Image from 'next/image';
import {DeepFooterProps, FooterProps, LogoContentProps} from './FooterProps';

const Footer: React.FC<FooterProps> = ({
	logoContainer,
	otherCols = [],
	deepFooterlinks,
	subdomain = '',
}) => {
	const cols12 = 12;
	const maxCols = 4;
	// -1 because logo is 1 col
	let lgCols = Math.round(cols12 / otherCols.length - 1);
	if (otherCols.length > maxCols) {
		lgCols = Math.round(cols12 / maxCols);
	}

	const colSpan = {
		base: cols12,
		md: 6,
		lg: subdomain === 'thegregory' ? 4 : lgCols,
	};
	const domainTextStyleXs =
		subdomain === 'thegregory' ? textStyles.textxsGillSans : textStyles.textxs;
	const domainTextStyleSm =
		subdomain === 'thegregory' ? textStyles.textsmGillSans : textStyles.textsm;

	return (
		<Box
			bg={subdomain === 'thegregory' ? colors.mist[900] : colors.grey[800]}
			color={colors.white.whiteAlpha}
			as='section'
			overflow='hidden'
			position='relative'>
			<Container maxW='container.xl'>
				<Grid
					templateColumns={{
						base: '1fr',
						md: 'repeat(12, 1fr)',
					}}
					gap={{base: '3.5rem'}}
					py={{base: '3rem', md: '4rem'}}>
					<GridItem colSpan={colSpan}>
						<LogoContent logoContainer={logoContainer} subdomain={subdomain} />
					</GridItem>

					{otherCols.map((col, index) => {
						return (
							<GridItem key={`col-${index}`} colSpan={colSpan}>
								{col?.title && col?.links?.length > 0 ? (
									<>
										<Text
											{...domainTextStyleXs}
											textTransform={'uppercase'}
											pb={{base: '1.5rem'}}>
											{col.title}
										</Text>

										<Box mt={2}>
											{col.links
												.filter((link) => {
													const hideGarden = process.env.NEXT_PUBLIC_HIDE_GARDEN === 'true';
													if (hideGarden) {
														return !link?.url?.includes('garden');
													}
													return true;
												})
												.map((link, i) => {
													return (
														<Box pb={{base: '1rem'}} key={`col-${i}`}>
															<Button
																className='footer-link'
																colorScheme={colors.white.whiteAlpha}
																as='a'
																href={link?.url}
																target={link?.target}
																variant='link'
																{...domainTextStyleSm}>
																{decode(link?.title)}
															</Button>
														</Box>
													);
												})}
										</Box>
									</>
								) : null}
							</GridItem>
						);
					})}
				</Grid>

				<DeepFooter deepFooterlinks={deepFooterlinks} subdomain={subdomain} />
			</Container>
		</Box>
	);
};
export default Footer;

const LogoContent: React.FC<LogoContentProps> = ({
	logoContainer,
	subdomain,
}) => {
	const domainTextStyleSm =
		subdomain === 'thegregory' ? textStyles.textsmGillSans : textStyles.textsm;
	const logoWidth = subdomain === 'thegregory' ? '176' : '64';
	const logoHeight = subdomain === 'thegregory' ? '186' : '80';

	return (
		<Grid>
			<Flex width='fit-content' flexDirection={'column'} alignItems={'center'}>
				{logoContainer?.image?.src ? (
					<Image
						src={logoContainer.image.src}
						alt={logoContainer?.logoText}
						width={logoWidth}
						height={logoHeight}
					/>
				) : null}
				<Text {...domainTextStyleSm} my={{base: '2rem'}}>
					{logoContainer?.logoText}
				</Text>
			</Flex>

			{logoContainer?.addressLines.map((addressLineItem, index) => (
				<Text {...domainTextStyleSm} key={`address-${index}`}>
					{addressLineItem}
				</Text>
			))}
		</Grid>
	);
};

const DeepFooter: React.FC<DeepFooterProps> = ({
	deepFooterlinks,
	subdomain,
}) => {
	const domainTextStyleSm =
		subdomain === 'thegregory' ? textStyles.textsmGillSans : textStyles.textsm;
	const domainTextStyleXs =
		subdomain === 'thegregory' ? textStyles.textxsGillSans : textStyles.textxs;
	const gridRepeatMd =
		subdomain === 'thegregory' ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)';

	return (
		<Grid
			className='deep-footer'
			templateColumns={{base: 'repeat(2, 1fr)', md: gridRepeatMd}}
			gap={{base: '3rem', md: '3.5rem'}}
			py={{base: '2rem'}}
			borderTop={`1px solid ${colors.grey[600]}`}>
			{deepFooterlinks?.map(({title, url, target}, i) => {
				if (!title) return null;

				const decodedTitle = decode(title);

				if (i === 0 || url === '/')
					return (
						<Box flex={{base: 1}} key={decodedTitle}>
							<Text {...domainTextStyleXs}>
								© {new Date().getFullYear()} {decodedTitle}
							</Text>
						</Box>
					);

				return (
					<Box flex={{base: 1}} key={decodedTitle}>
						<Button
							className='deep-footer-link'
							colorScheme={colors.white.whiteAlpha}
							as='a'
							href={url}
							target={target}
							variant='link'
							{...domainTextStyleSm}>
							{decodedTitle}
						</Button>
					</Box>
				);
			})}
		</Grid>
	);
};
